import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import WishlistItem from './WishlistItem';
import '@fontsource/ibm-plex-mono';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { closeWishlist, openLogin } from '../redux/slices/modalSlice';
import { addToCart } from '../redux/slices/cartSlice';
import { removeFromFavorites } from '../redux/slices/favoritesSlice';

const Container = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafa 100%);
  border-radius: 24px;
  padding: 32px;
  max-width: 720px;
  width: auto;
  margin: 0 auto;
  font-family: 'IBM Plex Mono', monospace;
  box-shadow: 0 8px 32px rgba(59, 98, 85, 0.12);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
    border-radius: 16px;
    max-height: 95vh;
  }
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #3B6255;
  margin: 0 0 32px 0;
  text-align: center;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 24px;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(59, 98, 85, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #3B6255 0%, #2d4a3d 100%);
    border-radius: 3px;
    
    &:hover {
      background: linear-gradient(180deg, #2d4a3d 0%, #1a2f23 100%);
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #3B6255 rgba(59, 98, 85, 0.1);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
`;

interface Book {
  item_id: number;
  book_id: number;
  book_title: string;
  author_name: string;
  book_price: number;
  book_cover_path: string;
  addedToCart: boolean;
}

const Wishlist: React.FC = () => {
  const customerId = useSelector((state: RootState) => state.auth.customer_id);
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAuthChecked = useSelector((state: RootState) => state.auth.isAuthChecked);
  const dispatch = useDispatch();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlist = async () => {
    try {
      const resWishlist = await fetch(`${process.env.REACT_APP_BASE_API_URL}/wishlist/customer/${customerId}?customerId=${customerId}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const wishlist = await resWishlist.json();

      if (!wishlist || wishlist.length === 0) {
        setBooks([]);
        setLoading(false);
        return;
      }

      const resShoppingCart = await fetch(`${process.env.REACT_APP_BASE_API_URL}/shopping-cart/customer/${customerId}?customerId=${customerId}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const shoppingCart = await resShoppingCart.json();

      const booksData: Book[] = [];
      for (const item of wishlist) {
        const resBook = await fetch(`${process.env.REACT_APP_BASE_API_URL}/books/${item.book_id}`);
        const bookDetails = await resBook.json();
        const resAuthor = await fetch(`${process.env.REACT_APP_BASE_API_URL}/authors/${bookDetails.author_id}`)

        const inCart = shoppingCart.some((cartItem: any) => cartItem.book_id === item.book_id);
        
        const authorData = await resAuthor.json();
        booksData.push({
          ...bookDetails,
          author_name: authorData.author_name,
          item_id: item.item_id,
          addedToCart: inCart,
        });
      }

      setBooks(booksData);
    } catch (err) {
      console.error('Помилка завантаження вішліста:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!customerId) return;

    fetchWishlist();
  }, [customerId, token]);

  const handleAddToCart = async (bookId: number) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/shopping-cart?customerId=${customerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_id: customerId,
          book_id: bookId,
          book_quantity: 1,
        }),
      });

      if (!res.ok) {
        throw new Error(`Помилка при додаванні: ${res.status}`);
      }

      const data = await res.json();
      dispatch(addToCart({
        id: data.book_id,
        quantity: data.book_quantity,
        item_id: data.item_id,
        customer_id: data.customer_id
      }));
      console.log('Додано в кошик:', data);
      fetchWishlist();
    } catch (error) {
      console.error('Не вдалося додати до кошика', error);
    }
  };

  const handleDelete = async (bookId: number) => {
    try {
      const item = books.find((wi: Book) => wi.book_id === bookId);
      if (!item) {
        console.warn('Цієї книги немає у вішлісті');
        return;
      }

      const resDelete = await fetch(`${process.env.REACT_APP_BASE_API_URL}/wishlist/item/${item.item_id}?customerId=${customerId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!resDelete.ok) throw new Error(`Помилка при видаленні: ${resDelete.status}`);

      const result = await resDelete.json();
      dispatch(removeFromFavorites(result.deleted.item_id));
      console.log('Видалено з вішліста:', result);
      fetchWishlist();
    } catch (error) {
      console.error('Не вдалося видалити з вішліста', error);
    }
  };

  if (isAuthChecked && !customerId) {
    dispatch(closeWishlist())
    dispatch(openLogin())
  }

  if (loading) {
    return <div style={{color: 'white'}}>Завантаження...</div>;
  }

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      <Title>Список бажань</Title>
      {books.length === 0 ? (
        <EmptyState>Ваш список бажань порожній</EmptyState>
      ) : (
        <ItemsList>
          {books.map(item => (
            <WishlistItem
              key={item.book_id}
              image={item.book_cover_path}
              title={item.book_title}
              author={item.author_name}
              price={item.book_price}
              addedToCart={item.addedToCart}
              onAddToCart={() => handleAddToCart(item.book_id)}
              onDelete={() => handleDelete(item.book_id)}
            />
          ))}
        </ItemsList>
      )}
    </Container>
  );
};

export default Wishlist;